"use client";

import { useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { gallery } from "@/content/gallery";
import { GalleryLightbox } from "@/components/sections/gallery-lightbox";
import { cn } from "@/lib/utils";

const FILTERS = ["all", "sale", "giardino", "piatti"] as const;
type Filter = (typeof FILTERS)[number];

function countOf(filter: Filter): number {
  return filter === "all" ? gallery.length : gallery.filter((item) => item.category === filter).length;
}

/**
 * Galleria a colonne (proporzioni naturali, verticali e orizzontali mescolate) con
 * filtri per tema e ingrandimento a schermo intero. Le colonne si riempiono dall'alto
 * verso il basso, quindi l'ordine in `content/gallery.ts` è già alternato per tema.
 */
export function GalleryExplorer() {
  const t = useTranslations("galleria");
  const locale = useLocale();
  const [filter, setFilter] = useState<Filter>("all");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const items = filter === "all" ? gallery : gallery.filter((item) => item.category === filter);

  return (
    <>
      <div role="group" aria-label={t("filterLabel")} className="flex flex-wrap gap-2.5">
        {FILTERS.map((id) => (
          <button
            key={id}
            type="button"
            aria-pressed={filter === id}
            onClick={() => setFilter(id)}
            className={cn(
              "flex min-h-11 items-center gap-2 rounded-sm border px-4 text-xs font-medium tracking-[0.14em] uppercase transition-colors duration-brand-fast focus-visible:ring-2 focus-visible:ring-rosso-pozzo/50 focus-visible:outline-none",
              filter === id
                ? "border-inchiostro bg-inchiostro text-avorio"
                : "border-pietra-light/70 text-inchiostro hover:border-inchiostro"
            )}
          >
            {t(`filters.${id}`)}
            <span className={cn("tabular-nums", filter === id ? "text-avorio/60" : "text-pietra")}>{countOf(id)}</span>
          </button>
        ))}
      </div>

      <ul className="mt-10 columns-2 gap-3 md:columns-3 md:gap-4 lg:columns-4">
        {items.map((item, index) => {
          const label = locale === "en" ? item.en : item.it;
          return (
            <li key={item.id} className="mb-3 break-inside-avoid md:mb-4">
              <button
                type="button"
                onClick={() => setOpenIndex(index)}
                aria-label={label}
                className="group relative block w-full cursor-zoom-in overflow-hidden rounded-lg bg-avorio-2 focus-visible:ring-2 focus-visible:ring-rosso-pozzo/60 focus-visible:outline-none"
              >
                <Image
                  src={item.src}
                  alt={label}
                  width={item.width}
                  height={item.height}
                  sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                  className="h-auto w-full transition-transform duration-700 ease-brand-out group-hover:scale-[1.04] motion-reduce:transition-none"
                />
              </button>
            </li>
          );
        })}
      </ul>

      <GalleryLightbox items={items} index={openIndex} onIndexChange={setOpenIndex} />
    </>
  );
}
